import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Target,
  Flame,
  Rocket,
  ClipboardCheck,
  Loader2,
  Clock,
  ChevronDown,
  ChevronUp,
  Zap,
  X,
  CheckCircle2,
} from "lucide-react";

const LEVEL = {
  green: {
    label: "Protected",
    icon: ShieldCheck,
    ring: "ring-emerald-500/40",
    border: "border-emerald-500/40",
    bg: "bg-gradient-to-br from-emerald-500/10 via-zinc-950 to-zinc-950",
    accent: "text-emerald-300",
    chip: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  },
  amber: {
    label: "Fragile",
    icon: ShieldAlert,
    ring: "ring-amber-500/40",
    border: "border-amber-500/40",
    bg: "bg-gradient-to-br from-amber-500/10 via-zinc-950 to-zinc-950",
    accent: "text-amber-300",
    chip: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  },
  red: {
    label: "Overloaded",
    icon: AlertTriangle,
    ring: "ring-red-500/40",
    border: "border-red-500/40",
    bg: "bg-gradient-to-br from-red-500/10 via-zinc-950 to-zinc-950",
    accent: "text-red-300",
    chip: "bg-red-500/15 text-red-200 border-red-500/40",
  },
};

const TIED_TO = {
  sprint: { icon: Rocket, label: "Sprint" },
  incident: { icon: AlertTriangle, label: "Clinical" },
  deadline: { icon: Clock, label: "Deadline" },
  habit: { icon: Target, label: "Habit" },
  recovery: { icon: Flame, label: "Recovery" },
};

export default function DailyBrief() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [committed, setCommitted] = useState(false);
  const [showSignals, setShowSignals] = useState(false);
  const [commitment, setCommitment] = useState(null);
  const [executing, setExecuting] = useState(false);

  const todayKey = new Date().toISOString().slice(0, 10);
  const commitKey = `niko_brief_commit_${todayKey}`;
  const cacheKey = `niko_brief_cache_${todayKey}`;

  const fetchBrief = useCallback(async (fresh = false) => {
    setLoading(true);
    setErr("");
    try {
      if (!fresh) {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          setBrief(JSON.parse(cached));
          setLoading(false);
          return;
        }
      }
      const res = await axios.get(`${API}/synthesis/morning`, { withCredentials: true });
      setBrief(res.data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch (_) { /* ignore */ }
    } catch (e) {
      setErr("Couldn't generate your brief right now.");
    } finally {
      setLoading(false);
    }
  }, [cacheKey]);

  const fetchCommitment = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/synthesis/commitment`, { withCredentials: true });
      const c = res.data && res.data.commitment_id ? res.data : null;
      setCommitment(c);
    } catch (_) {
      setCommitment(null);
    }
  }, []);

  useEffect(() => {
    fetchBrief(false);
    fetchCommitment();
    setCommitted(localStorage.getItem(commitKey) === "1");
  }, [fetchBrief, fetchCommitment, commitKey]);

  const refresh = () => {
    try { sessionStorage.removeItem(cacheKey); } catch (_) { /* ignore */ }
    fetchBrief(true);
  };

  const toggleCommit = () => {
    const next = !committed;
    setCommitted(next);
    if (next) localStorage.setItem(commitKey, "1");
    else localStorage.removeItem(commitKey);
  };

  const runBet = async (durationMinutes = 90) => {
    if (!brief?.one_bet?.action) return;
    setExecuting(true);
    try {
      // Find a task_title reference from the signals (first matching deadline/task)
      const oneBet = brief.one_bet;
      const sig = brief.signals || {};
      let taskTitle = null;
      if (oneBet.tied_to === "deadline" && sig.deadlines_next_7?.length) {
        // Prefer a deadline mentioned in the action text, else the soonest
        const soonest = [...sig.deadlines_next_7].sort((a, b) => a.days_until - b.days_until)[0];
        taskTitle = soonest?.title;
      } else if (sig.tasks?.titles?.length) {
        taskTitle = sig.tasks.titles[0];
      }

      const res = await axios.post(
        `${API}/synthesis/execute`,
        {
          action: oneBet.action,
          tied_to: oneBet.tied_to,
          rationale: oneBet.rationale,
          task_title: taskTitle,
          duration_minutes: durationMinutes,
        },
        { withCredentials: true }
      );

      if (res.data?.scheduled) {
        setCommitment(res.data.commitment);
        toast.success(`Focus block scheduled ${res.data.start_time}–${res.data.end_time}`);
        if (!committed) {
          setCommitted(true);
          localStorage.setItem(commitKey, "1");
        }
      } else {
        // Try again with shorter duration
        if (durationMinutes > 45) {
          toast.message("No 90-min window open — trying 45 minutes...");
          return runBet(45);
        }
        toast.error(res.data?.reason || "No open window today");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule the bet");
    } finally {
      setExecuting(false);
    }
  };

  const cancelCommitment = async () => {
    setExecuting(true);
    try {
      await axios.delete(`${API}/synthesis/commitment`, { withCredentials: true });
      setCommitment(null);
      toast.success("Focus block cancelled");
    } catch (_) {
      toast.error("Could not cancel");
    } finally {
      setExecuting(false);
    }
  };

  const level = LEVEL[brief?.threat_level] || LEVEL.green;
  const LevelIcon = level.icon;
  const tied = TIED_TO[brief?.one_bet?.tied_to] || TIED_TO.habit;
  const TiedIcon = tied.icon;

  return (
    <Card
      className={`relative overflow-hidden ${level.border} ${level.bg} border-2 shadow-2xl`}
      data-testid="daily-brief-card"
    >
      {/* Ambient glow */}
      <div className={`absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-30 ${
        brief?.threat_level === "red" ? "bg-red-500" :
        brief?.threat_level === "amber" ? "bg-amber-500" : "bg-emerald-500"
      }`} />

      <CardContent className="relative pt-5 pb-5 space-y-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg bg-zinc-900/80 ${level.ring} ring-1 flex-shrink-0`}>
              <Sparkles className={`w-5 h-5 ${level.accent}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  Niko's Morning Brief
                </span>
                {brief && (
                  <Badge variant="outline" className={`${level.chip} text-[10px] uppercase font-bold px-2`} data-testid="brief-threat-level">
                    <LevelIcon className="w-3 h-3 mr-1" />
                    {level.label}
                  </Badge>
                )}
              </div>
              {brief?.signals && (
                <div className="text-xs text-zinc-400 mt-0.5">
                  {brief.signals.weekday} · {brief.signals.date}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="icon"
              variant="ghost"
              onClick={refresh}
              disabled={loading}
              className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="brief-refresh-btn"
              title="Regenerate"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading && !brief && (
          <div className="py-8 flex items-center gap-2 text-sm text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Synthesizing your day...
          </div>
        )}

        {err && !brief && (
          <div className="text-sm text-red-300" data-testid="brief-error">{err}</div>
        )}

        {brief && (
          <>
            {/* Reality */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">Reality</div>
              <p className="text-white text-[15px] leading-relaxed" data-testid="brief-reality">
                {brief.reality}
              </p>
            </div>

            {/* Leverage Point */}
            <div className="border-l-2 border-zinc-700 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Leverage Point</div>
              <p className={`${level.accent} text-sm font-medium leading-snug`} data-testid="brief-leverage">
                {brief.leverage_point}
              </p>
            </div>

            {/* One Bet */}
            <div className={`rounded-xl bg-zinc-900/70 border ${level.border} p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Today's One Bet</span>
                <Badge variant="outline" className="text-[10px] uppercase bg-zinc-800 border-zinc-700 text-zinc-200">
                  <TiedIcon className="w-3 h-3 mr-1" /> {tied.label}
                </Badge>
              </div>
              <p className="text-white text-base font-semibold leading-snug mb-2" data-testid="brief-bet-action">
                {brief.one_bet?.action}
              </p>
              {brief.one_bet?.rationale && (
                <p className="text-xs text-zinc-400 leading-relaxed" data-testid="brief-bet-rationale">
                  {brief.one_bet.rationale}
                </p>
              )}

              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800 flex-wrap">
                <Button
                  size="sm"
                  onClick={toggleCommit}
                  className={committed
                    ? "bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                    : "bg-zinc-800 text-white hover:bg-zinc-700 text-xs border border-zinc-700"
                  }
                  data-testid="brief-commit-btn"
                >
                  {committed ? (
                    <><ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />Committed</>
                  ) : (
                    <><Target className="w-3.5 h-3.5 mr-1.5" />Commit</>
                  )}
                </Button>

                {!commitment ? (
                  <Button
                    size="sm"
                    onClick={() => runBet(90)}
                    disabled={executing}
                    className="bg-white text-black hover:bg-zinc-100 text-xs font-semibold"
                    data-testid="brief-run-btn"
                  >
                    {executing ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Scheduling...</>
                    ) : (
                      <><Zap className="w-3.5 h-3.5 mr-1.5" />Run my bet</>
                    )}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-emerald-300 font-semibold" data-testid="brief-scheduled">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>
                      Focus block {commitment.start_time}–{commitment.end_time}
                      {(() => {
                        const [h, m] = (commitment.start_time || "00:00").split(":").map(Number);
                        const now = new Date();
                        const target = new Date();
                        target.setHours(h || 0, m || 0, 0, 0);
                        const diffMin = Math.round((target - now) / 60000);
                        if (diffMin > 0 && diffMin <= 60) return <span className="ml-2 text-white/70 font-medium">· starts in {diffMin} min</span>;
                        if (diffMin <= 0 && diffMin >= -((commitment.duration_minutes || 0))) return <span className="ml-2 text-amber-300 font-medium">· in progress</span>;
                        return null;
                      })()}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={cancelCommitment}
                      disabled={executing}
                      className="h-6 w-6 text-zinc-500 hover:text-red-300"
                      title="Cancel focus block"
                      data-testid="brief-cancel-bet"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}

                <button
                  onClick={() => setShowSignals((s) => !s)}
                  className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                  data-testid="brief-show-signals"
                >
                  {showSignals ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Signals
                </button>
              </div>
            </div>

            {/* Signals (collapsed by default) */}
            {showSignals && brief.signals && (
              <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-3 text-xs space-y-2" data-testid="brief-signals">
                <SignalRow label="Yesterday" value={
                  brief.signals.yesterday.had_checkout
                    ? `Energy ${brief.signals.yesterday.energy_level ?? "—"}/5${brief.signals.yesterday.mood ? ` · ${brief.signals.yesterday.mood}` : ""}`
                    : "No checkout logged"
                } />
                <SignalRow label="Energy (7d)" value={
                  `avg ${brief.signals.energy_7d.avg ?? "—"}/5 · trend ${brief.signals.energy_7d.trend}${brief.signals.energy_7d.burnout_streak ? ` · burnout streak ${brief.signals.energy_7d.burnout_streak}` : ""}`
                } />
                <SignalRow label="Calendar" value={
                  `${brief.signals.calendar.meeting_count} meetings · ${brief.signals.calendar.meeting_hours}h`
                } />
                <SignalRow label="Open high-priority" value={`${brief.signals.tasks.high_priority_count} tasks`} />
                <SignalRow label="Incidents (7d)" value={
                  `${brief.signals.incidents.last_7d} logged · ${brief.signals.incidents.unreviewed} unreviewed${brief.signals.incidents.hot_unit ? ` · hot: ${brief.signals.incidents.hot_unit}` : ""}${brief.signals.incidents.hot_type ? ` / ${brief.signals.incidents.hot_type}` : ""}`
                } />
                {brief.signals.sprint.active_item_count > 0 && (
                  <SignalRow label="Sprint" value={`${brief.signals.sprint.active_item_count} active items`} />
                )}
                {brief.signals.aoc.on_call_today && (
                  <SignalRow label="AOC" value="On call today" />
                )}
                {brief.signals.deadlines_next_7.length > 0 && (
                  <SignalRow label="Deadlines" value={
                    brief.signals.deadlines_next_7.slice(0, 3).map((d) => `${d.title} (${d.days_until}d)`).join(" · ")
                  } />
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SignalRow({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-28 flex-shrink-0 pt-0.5">{label}</div>
      <div className="text-zinc-200 flex-1">{value}</div>
    </div>
  );
}
